import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { 
  Package, Plus, Minus, Search, AlertTriangle, TrendingUp, TrendingDown,
  Warehouse, Settings, Bell, Loader2, Edit, Trash2, ArrowUpCircle, 
  ArrowDownCircle, History, DollarSign, Filter, User, Building2, ShirtIcon,
  RotateCcw, Boxes, X
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { formatCurrency } from "@/lib/config";
import { toast } from "sonner";
import api from "@/lib/api";
import { VetementsInventory } from "@/components/VetementsInventory";
import { useAuth } from "@/lib/AuthContext";
import { usePermissions } from "@/lib/PermissionsContext";
import { useKioskMode } from "@/hooks/useKioskMode";
import KioskScanMode from "@/components/KioskScanMode";
import CorbeilleArticlesDialog from "@/components/CorbeilleArticlesDialog";
import { LogOut, Printer } from "lucide-react";
import { dymoPrintLabelAuto, dymoPreviewLabel, dymoBulkPrintLabels, dymoPrintFreeText } from "@/lib/dymoPrint";
import NormesArticleSection from "@/components/NormesArticleSection";
import { urlFichier } from '@/lib/fichiers';

const CATEGORIES = [
  { value: "peinture", label: "Peinture", icon: "🎨" },
  { value: "materiaux", label: "Matériaux", icon: "🧱" },
  { value: "equipements", label: "Équipements", icon: "🔧" },
  { value: "autre", label: "Autre", icon: "📦" }
];

const DEPARTMENTS = ["PRQC", "LMDS", "Location", "GLOBAL"];

const UNITS = ["unité", "gallon", "litre", "sachet", "rouleau", "boîte", "pied", "mètre", "kg"];

const ROLES = [
  { value: "peintre", label: "Peintre" },
  { value: "installateur", label: "Installateur" },
  { value: "chef_equipe", label: "Chef d'équipe" },
  { value: "autre", label: "Autre" }
];

export const Entrepot = () => {
  const { user, isChefEquipe, isProprietaire, isInstallateur, isAdmin } = useAuth();
  const { canDo } = usePermissions();
  // 🆕 [Phase v13.62 BLOC 1.2 — 2026-02-26] Hotfix hoisting : `canDo` doit être
  //   déstructuré AVANT toute utilisation. Avant ce fix, `canPrintLabels` utilisait
  //   `canDo(...)` à la ligne 4 alors que `usePermissions()` était appelé à la ligne 8
  //   → `ReferenceError: Cannot access 'canDo' before initialization` → ErrorBoundary
  //   affichait « Une erreur est survenue » en boucle. RCA : RCA_V13_62_REGRESSIONS_RBAC.md.
  // 🔀 [Phase v13.61 Vague 5b — 2026-08-25] Branchement matrice RBAC.
  //   Les 3 clés `entrepot_imprimer_*` sont désormais gates via `canDo()`. Comportement
  //   préservé par le seed Vague 5a (charge_projet + adjoint_admin + owner déjà True).
  const canPrintLabels = canDo('entrepot_imprimer_etiquette_produit')
                      || canDo('entrepot_imprimer_etiquettes_lot')
                      || canDo('entrepot_imprimer_etiquette_texte_libre');
  const { isKiosk, kioskEmployee, kioskLogout, remainingSeconds } = useKioskMode();
  const canEditStock = canDo('entrepot_modifier_stocks');
  const produitsSeulm = !canEditStock && canDo('entrepot_produits_seulement');
  const retraitSeulm = !canEditStock && canDo('entrepot_retrait_seulement');
  const canVoirStats = canDo('entrepot_voir_stats');
  const canVoirAlertes = canDo('entrepot_voir_alertes');
  // 🆕 2026-04-27 : Permissions équipements (sortie/remise)
  const canSortirEquip = isProprietaire || canDo('entrepot_equipements_sortie');
  // 🆕 2026-05-14 — Symétrie sortie/remise : si l'utilisateur peut SORTIR un équipement
  //   réutilisable, il doit aussi pouvoir le REMETTRE. Sinon, les chefs d'équipe se
  //   retrouvent avec des items sortis qu'ils ne peuvent pas retourner eux-mêmes.
  const canRemettreEquip = isProprietaire || canDo('entrepot_equipements_retour') || canDo('entrepot_equipements_sortie');
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [movements, setMovements] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [projets, setProjets] = useState([]);
  // 🆕 2026-05-14 — Recherche libre dans le dropdown "Lier à un projet" du dialog Sortie de stock
  const [projetSearch, setProjetSearch] = useState("");
  const [employeeData, setEmployeeData] = useState(null);
  
  // 🆕 Déterminer le département de l'utilisateur
  const userDepartment = employeeData?.departement || user?.departement || null;
  // Département pré-sélectionné mais non verrouillé : l'utilisateur peut toujours changer
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [showLowStock, setShowLowStock] = useState(false);
  // 🆕 2026-05-31 — Sélection multiple pour impression d'étiquettes en lot.
  // Set<string> contenant les IDs produits cochés.
  const [selectedProductIds, setSelectedProductIds] = useState(new Set());
  // 🖨 [Phase v13.60.10 — 2026-08-15] Impression de texte libre
  //   Modale ouverte via bouton à côté de "Imprimer toutes les étiquettes".
  //   Réutilise EXACTEMENT le même pipeline Dymo (89×36mm landscape).
  const [showFreeTextModal, setShowFreeTextModal] = useState(false);
  const [freeTextValue, setFreeTextValue] = useState("");
  const [freeTextPrinting, setFreeTextPrinting] = useState(false);
  // ✅ Contrôler l'onglet actif pour que "Voir tout" fonctionne depuis n'importe quelle vue
  const [activeTab, setActiveTab] = useState("products");
  // ✅ Vêtements en stock bas (widget centre de commande)
  const [vetementsLowStock, setVetementsLowStock] = useState([]);
  // ✅ Inventaire complet items sous quotas (pour stats de valorisation)
  const [vetementsStockAll, setVetementsStockAll] = useState([]);
  const [vetementModels, setVetementModels] = useState([]);
  
  // Dialogs
  const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);
  const [isMovementDialogOpen, setIsMovementDialogOpen] = useState(false);
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  // 🗑️ [Phase 19A] Corbeille articles supprimés
  const [isCorbeilleOpen, setIsCorbeilleOpen] = useState(false);
  const [corbeilleCount, setCorbeilleCount] = useState(0);
  const [editingProduct, setEditingProduct] = useState(null);
  const [movementProduct, setMovementProduct] = useState(null);
  const [movementType, setMovementType] = useState("out");
  
  // Alert config
  const [alertConfig, setAlertConfig] = useState({ email: "", enabled: false, frequency: "instant" });
  
  // New product form
  const [newProduct, setNewProduct] = useState({
    name: "", category: "peinture", department: "GLOBAL", unit: "unité",
    quantity: 0, min_threshold: 5, unit_price: 0, location: "", supplier: "",
    product_code: "", phone_number: "", notes: "",
    // 🆕 Bacs / Kits et équipements réutilisables
    is_kit: false, kit_items: [], is_returnable: false
  });
  
  // Movement form
  const [newMovement, setNewMovement] = useState({
    quantity: 1, taken_by: "", role: "peintre", department: "PRQC",
    project: "", projet_id: "", supplier: "", invoice_number: "", unit_cost: 0, notes: ""
  });

  // 🆕 Équipements actuellement sortis (pour remise)
  const [equipementsSortis, setEquipementsSortis] = useState([]);
  const [isReturnDialogOpen, setIsReturnDialogOpen] = useState(false);
  const [returnItem, setReturnItem] = useState(null);
  const [returnQty, setReturnQty] = useState(1);
  const [returnNotes, setReturnNotes] = useState("");

  useEffect(() => {
    loadData();
    loadEmployeeData();
    // 🗑️ [Phase 19A] Charger le count corbeille (owner uniquement)
    if (isProprietaire) {
      axios.get(`${api.baseUrl}/inventory/products/corbeille`)
        .then(r => setCorbeilleCount(r.data?.total || 0))
        .catch(() => setCorbeilleCount(0));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 🆕 Charger les données de l'employé pour connaître son département
  const loadEmployeeData = async () => {
    if (!user?.employee_id) return;
    try {
      const res = await axios.get(`${api.baseUrl}/employees/${user.employee_id}`);
      if (res.status === 200) {
        setEmployeeData(res.data);
        // Mettre à jour le filtre département si verrouillé
        // 🐛 2026-05-19 — En mode Kiosque Entrepôt, NE PAS auto-filtrer par département :
        //   - Le PC d'entrepôt est partagé (boss + employés). Il faut que TOUS les
        //     produits (PRQC, LMDS, Location, GLOBAL) soient visibles par défaut.
        //   - Sans ce garde-fou, un employé "peintre" connecté au kiosque voyait
        //     seulement les produits PRQC et croyait que l'inventaire avait disparu.
        const dept = res.data.departement;
        if (dept && !isProprietaire && !isChefEquipe && !isKiosk) {
          setDepartmentFilter(dept === "LMDS" ? "LMDS" : "PRQC");
        }
      }
    } catch (err) {
      console.error('Erreur chargement employé:', err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // 🆕 [Phase v13.60.8 — 2026-08-14] Défense en profondeur : chaque appel
      // du Promise.all a désormais son propre `.catch()` avec fallback safe +
      // warning console. Avant : si UN endpoint échouait (ex : /inventory/alert-config
      // renvoyait 403 pour adjoint_admin), toute la Promise.all cassait → toast
      // "Erreur lors du chargement" + écran totalement vide. Ce pattern rend le
      // composant robuste : si un endpoint futur échoue, la page continue de
      // charger avec les autres données disponibles.
      // Cf. /app/memory/rca_v13_60_8_kiosque_entrepot.md §4.3.
      const _warn = (name) => (err) => {
        console.warn(`[Entrepot v13.60.8] fetch "${name}" a échoué, fallback appliqué:`, err?.response?.status || err?.message);
        return null;
      };
      const [productsRes, statsRes, movementsRes, alertsRes, configRes, projetsData, vetInvRes, vetModelsRes, sortisRes] = await Promise.all([
        axios.get(`${api.baseUrl}/inventory/products`).catch(_warn('products')),
        axios.get(`${api.baseUrl}/inventory/stats`).catch(_warn('stats')),
        axios.get(`${api.baseUrl}/inventory/movements?limit=20`).catch(_warn('movements')),
        axios.get(`${api.baseUrl}/inventory/alerts`).catch(_warn('alerts')),
        axios.get(`${api.baseUrl}/inventory/alert-config`).catch(_warn('alert-config')),
        axios.get(`${api.baseUrl}/crm/dossiers`)
          .then(r => r.status === 200 ? r.data : { dossiers: [] })
          .catch(() => ({ dossiers: [] })),
        axios.get(`${api.baseUrl}/vetements/inventory`).catch(() => ({ data: { inventory: [] } })),
        axios.get(`${api.baseUrl}/vetements/models`).catch(() => ({ data: { models: [] } })),
        axios.get(`${api.baseUrl}/inventory/equipements-sortis`).catch(() => ({ data: { items: [] } }))
      ]);
      
      // Fallbacks safe si un endpoint principal a échoué (null)
      const productsData = productsRes?.data ?? { products: [] };
      const statsData = statsRes?.data ?? {};
      const movementsData = movementsRes?.data ?? { movements: [] };
      const alertsData = alertsRes?.data ?? { alerts: [] };
      const configData = configRes?.data ?? { email: "", enabled: false, frequency: "instant" };
      
      setProducts(productsData.products || []);
      setStats(statsData);
      setMovements(movementsData.movements || []);
      setAlerts(alertsData.alerts || []);
      setEquipementsSortis(sortisRes?.data?.items || []);

      // ✅ Vêtements avec stock <= seuil d'alerte
      const vetInv = (vetInvRes?.data?.inventory || []);
      setVetementsStockAll(vetInv);
      setVetementModels(vetModelsRes?.data?.models || []);
      const vetLow = vetInv.filter(
        v => (v.quantite_stock ?? 0) <= (v.seuil_alerte ?? 10)
      );
      setVetementsLowStock(vetLow);
      
      // 🆕 2026-05-14 — Plus de limite à 3 projets pour le chef_equipe.
      //   On garde TOUS les projets actifs (filtrage par équipe optionnel via UI).
      //   La recherche libre est ajoutée dans le dialog "Sortie de stock"
      //   pour qu'on puisse trouver n'importe quel projet, même non assigné à l'équipe.
      const STATUTS_EXCLUS = ['refuse', 'ferme', 'prospect'];
      let allProjets = (projetsData.dossiers || []).filter(p => !STATUTS_EXCLUS.includes(p.statut));
      allProjets = allProjets.sort((a, b) => (a.date_debut_prevue || '').localeCompare(b.date_debut_prevue || ''));
      setProjets(allProjets);
      
      // Pré-remplir avec l'email admin si la config est vide
      setAlertConfig({
        email: "admin@peintureresidentielleqc.com",
        enabled: true,
        frequency: "instant",
        ...configData,
        // S'assurer que l'email n'est jamais vide
        email: configData?.email || "admin@peintureresidentielleqc.com",
      });
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const saveProduct = async () => {
    if (!newProduct.name) {
      toast.error("Le nom du produit est requis");
      return;
    }
    
    try {
      const payload = editingProduct ? { ...newProduct, id: editingProduct.id } : newProduct;
      const response = await axios.post(`${api.baseUrl}/inventory/products`, payload);
      
      if (response.status === 200) {
        toast.success(editingProduct ? "Produit modifié" : "Produit ajouté");
        setIsProductDialogOpen(false);
        setEditingProduct(null);
        setNewProduct({
          name: "", category: "peinture", department: "GLOBAL", unit: "unité",
          quantity: 0, min_threshold: 5, unit_price: 0, location: "", supplier: "",
          product_code: "", phone_number: "", notes: "",
          is_kit: false, kit_items: [], is_returnable: false
        });
        loadData();
      }
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const deleteProduct = async (productId) => {
    if (!confirm("Supprimer ce produit?")) return;
    
    try {
      await axios.delete(`${api.baseUrl}/inventory/products/${productId}`);
      toast.success("Produit envoyé à la corbeille", {
        description: isProprietaire ? "Vous pouvez le restaurer depuis le bouton Corbeille." : undefined,
      });
      // 🗑️ [Phase 19A] Incrémenter le count corbeille (owner uniquement)
      if (isProprietaire) setCorbeilleCount(c => c + 1);
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // 🖨 2026-05-19 — Imprime une étiquette code-barres (Code128) sur la Dymo
  // LabelWriter 550 via Dymo Connect Web Service (https://localhost:41951).
  // Le code-barres encode l'`id` UUID du produit → c'est ce que le scanner
  // renverra ensuite à `/api/entrepot/scan/mouvement`.
  //
  // 💡 Shift+Clic : ouvre une PRÉVISUALISATION (image PNG dans un nouvel
  //   onglet) sans envoyer à l'imprimante. Pratique pour valider le rendu
  //   avant de gâcher du papier.
  const imprimerEtiquette = async (product, evt) => {
    const isPreview = evt && (evt.shiftKey || evt.altKey);
    const payload = {
      productName: product.name || product.nom || 'Produit',
      barcodeValue: product.id,
    };
    if (isPreview) {
      dymoPreviewLabel(payload);
      toast.info("Aperçu ouvert dans un nouvel onglet");
      return;
    }
    try {
      const result = await dymoPrintLabelAuto(payload);
      // 🆕 2026-02-21 — Message adapté à la méthode utilisée
      if (result?.method === 'inapp_modal' || result?.method === 'browser_preview') {
        toast.info(
          `🪟 Aperçu ouvert : ${product.name}\nCliquez le bouton vert puis sélectionnez Dymo LabelWriter 550.`,
          { duration: 9000 }
        );
      } else {
        toast.success(`🖨 Étiquette imprimée : ${product.name}`);
      }
    } catch (e) {
      toast.error(e?.message || "Échec de l'impression. Dymo Connect actif ?", {
        duration: 8000,
      });
    }
  };

  // 🆕 2026-05-31 — Impression EN LOT (sélection ou tous les produits filtrés).
  //   Méthode = dymoBulkPrintLabels (browser print dialog avec @page 36×89mm).
  //   Toutes les étiquettes sortent d'un coup, une par page.
  const imprimerEtiquettesEnLot = (products) => {
    const items = (products || [])
      .filter(p => p && p.id)
      .map(p => ({ productName: p.name || p.nom || 'Produit', barcodeValue: p.id }));
    if (items.length === 0) {
      toast.error("Aucun produit sélectionné");
      return;
    }
    try {
      dymoBulkPrintLabels(items);
      toast.success(`🖨 ${items.length} étiquette${items.length > 1 ? 's' : ''} envoyée${items.length > 1 ? 's' : ''} à l'impression`);
    } catch (e) {
      toast.error(e?.message || "Échec impression en lot");
    }
  };

  // 🖨 [Phase v13.60.10 — 2026-08-15] Impression d'une étiquette de texte libre.
  //   Réutilise EXACTEMENT le pipeline Dymo (89×36mm landscape, iframe cachée).
  //   Même imprimante, même format, mêmes rôles autorisés (canPrintLabels).
  const imprimerTexteLibre = () => {
    const texte = (freeTextValue || '').trim();
    if (!texte) {
      toast.error("Saisissez un texte à imprimer");
      return;
    }
    setFreeTextPrinting(true);
    try {
      const result = dymoPrintFreeText({ text: texte });
      toast.success(
        `🖨 Étiquette texte envoyée à l'impression (${result.lines} ligne${result.lines > 1 ? 's' : ''}, ${result.fontSize}px)`
      );
      setShowFreeTextModal(false);
      setFreeTextValue("");
    } catch (e) {
      toast.error(e?.message || "Échec impression texte libre");
    } finally {
      setFreeTextPrinting(false);
    }
  };

  // Toggle sélection d'un produit
  const toggleProductSelection = (productId) => {
    setSelectedProductIds(prev => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId); else next.add(productId);
      return next;
    });
  };
  // Sélectionner / désélectionner tout
  const allVisibleSelected = () => {
    if (!Array.isArray(filteredProducts) || filteredProducts.length === 0) return false;
    return filteredProducts.every(p => selectedProductIds.has(p.id));
  };
  const toggleSelectAllVisible = () => {
    setSelectedProductIds(prev => {
      const next = new Set(prev);
      if (allVisibleSelected()) {
        filteredProducts.forEach(p => next.delete(p.id));
      } else {
        filteredProducts.forEach(p => next.add(p.id));
      }
      return next;
    });
  };

  const recordMovement = async () => {
    if (!movementProduct || newMovement.quantity <= 0) {
      toast.error("Quantité invalide");
      return;
    }
    
    try {
      const payload = {
        product_id: movementProduct.id,
        movement_type: movementType,
        quantity: parseFloat(newMovement.quantity),
        date: new Date().toISOString().split("T")[0],
        ...newMovement
      };
      
      // Installateur: lier automatiquement à la semaine en cours
      if (isInstallateur && movementType === 'out') {
        const now = new Date();
        const oneJan = new Date(now.getFullYear(), 0, 1);
        const currentWeek = Math.ceil(((now - oneJan) / 86400000 + oneJan.getDay() + 1) / 7);
        payload.semaine_lmds = currentWeek;
        payload.annee_lmds = now.getFullYear();
        payload.taken_by = user?.prenom ? `${user.prenom} ${user.nom}` : '';
        payload.department = 'LMDS';
      }
      
      const response = await axios.post(`${api.baseUrl}/inventory/movements`, payload);
      
      const data = response.data;
      
      if (response.status === 200) {
        toast.success(movementType === "in" ? "Entrée enregistrée" : "Sortie enregistrée");
        setIsMovementDialogOpen(false);
        setMovementProduct(null);
        setNewMovement({
          quantity: 1, taken_by: "", role: "peintre", department: "PRQC",
          project: "", projet_id: "", supplier: "", invoice_number: "", unit_cost: 0, notes: ""
        });
        loadData();
      } else {
        toast.error(data.detail || "Erreur");
      }
    } catch (error) {
      console.error("Error recording movement:", error);
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const saveAlertConfig = async () => {
    try {
      await axios.post(`${api.baseUrl}/inventory/alert-config`, alertConfig);
      toast.success("Configuration sauvegardée");
      setIsConfigDialogOpen(false);
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  // 🆕 Ouvrir dialog de remise pour un équipement sorti
  const openReturnDialog = (item) => {
    setReturnItem(item);
    setReturnQty(item.solde || 1);
    setReturnNotes("");
    setIsReturnDialogOpen(true);
  };

  // 🆕 Enregistrer la remise (type "return" — crédite le stock)
  const recordReturn = async () => {
    if (!returnItem || returnQty <= 0) {
      toast.error("Quantité invalide");
      return;
    }
    if (returnQty > (returnItem.solde || 0)) {
      toast.error(`Impossible de remettre plus que ${returnItem.solde} (solde sorti)`);
      return;
    }
    try {
      const payload = {
        product_id: returnItem.product_id,
        movement_type: "return",
        quantity: parseFloat(returnQty),
        date: new Date().toISOString().split("T")[0],
        taken_by: user ? `${user.prenom || ''} ${user.nom || ''}`.trim() : "",
        role: user?.role || "",
        department: user?.departement || "PRQC",
        project: returnItem.project || "",
        projet_id: returnItem.projet_id || null,
        related_out_id: returnItem.last_out_id || null,
        notes: returnNotes || "Remise d'équipement"
      };
      const response = await axios.post(`${api.baseUrl}/inventory/movements`, payload);
      if (response.status === 200) {
        toast.success(`Équipement remis — ${returnItem.product_name} (+${returnQty})`);
        setIsReturnDialogOpen(false);
        setReturnItem(null);
        loadData();
      }
    } catch (error) {
      console.error("Erreur remise:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la remise");
    }
  };

  const openMovementDialog = (product, type) => {
    setMovementProduct(product);
    setMovementType(type);
    setNewMovement({
      quantity: 1,
      taken_by: user ? `${user.prenom || ''} ${user.nom || ''}`.trim() : "",
      role: user?.role || "peintre",
      department: product.department || user?.departement || "PRQC",
      project: "", projet_id: "",
      supplier: "", invoice_number: "", unit_cost: product.unit_price || 0, notes: ""
    });
    setIsMovementDialogOpen(true);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setNewProduct({ ...product });
    setIsProductDialogOpen(true);
  };

  // Filter products
  const filteredProducts = products.filter(p => {
    if (searchTerm && !p.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (categoryFilter !== "all" && p.category !== categoryFilter) return false;
    if (departmentFilter !== "all" && p.department !== departmentFilter) return false;
    if (showLowStock && p.quantity > p.min_threshold) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="entrepot-page">
      {/* 🏭 2026-05-18 — Bandeau Mode Kiosque Entrepôt (terminal partagé)
          Affiche en GRAND le nom de l'employé connecté + bouton Déconnexion
          + compte à rebours d'inactivité (auto-logout après 5 min). */}
      {isKiosk && (kioskEmployee || user) && (
        <div
          className="rounded-2xl bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 text-white p-5 sm:p-7 shadow-xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          data-testid="kiosk-header"
        >
          <div className="flex items-center gap-4 min-w-0">
            {(kioskEmployee?.photo_url || user?.photo_url) ? (
              <img
                src={urlFichier(kioskEmployee?.photo_url || user?.photo_url)}
                alt=""
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-4 border-white/40 shadow-lg shrink-0"
              />
            ) : (
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/20 flex items-center justify-center border-4 border-white/40 shadow-lg shrink-0">
                <User className="w-9 h-9 sm:w-10 sm:h-10 text-white" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-xs sm:text-sm uppercase tracking-wider text-amber-100 font-semibold">Mode Kiosque Entrepôt</p>
              <h2
                className="text-2xl sm:text-4xl font-bold leading-tight truncate"
                data-testid="kiosk-employee-name"
              >
                {kioskEmployee?.nom_complet
                  || `${kioskEmployee?.prenom || user?.prenom || ''} ${kioskEmployee?.nom || user?.nom || ''}`.trim()
                  || 'Employé'}
              </h2>
              {(kioskEmployee?.numero_employe || user?.numero_employe) && (
                <p className="text-sm text-amber-50/90 mt-0.5">
                  N° employé : <span className="font-mono font-semibold">{kioskEmployee?.numero_employe || user?.numero_employe}</span>
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="text-right">
              <p className="text-xs uppercase tracking-wider text-amber-100">Déconnexion auto</p>
              <p
                className="text-lg sm:text-xl font-mono font-bold tabular-nums"
                data-testid="kiosk-countdown"
              >
                {Math.floor((remainingSeconds || 0) / 60).toString().padStart(2, '0')}:
                {((remainingSeconds || 0) % 60).toString().padStart(2, '0')}
              </p>
            </div>
            <Button
              onClick={() => kioskLogout('manual')}
              variant="secondary"
              className="bg-white/90 text-orange-700 hover:bg-white font-semibold h-12 px-4 sm:px-6 text-base shadow-md"
              data-testid="kiosk-logout-btn"
            >
              <LogOut className="w-5 h-5 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      )}

      {/* 🔫 2026-05-19 — Mode Scan rapide (scanner code-barres + impression Dymo).
          Affiché uniquement en mode Kiosque. Permet de sortir/remettre des
          produits 1-par-1 en scannant les étiquettes. */}
      {isKiosk && (
        <KioskScanMode onMovementDone={() => loadData && loadData()} />
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Entrepôt</h1>
          <p className="text-sm text-slate-500">Gestion de l&apos;inventaire et du stock</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* 🗑️ [Phase 19A] Corbeille — owner uniquement */}
          {isProprietaire && (
            <Button
              variant="outline"
              onClick={() => setIsCorbeilleOpen(true)}
              data-testid="btn-open-corbeille"
              className="text-slate-600"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Corbeille
              {corbeilleCount > 0 && (
                <Badge variant="secondary" className="ml-2" data-testid="corbeille-badge-count">
                  {corbeilleCount}
                </Badge>
              )}
            </Button>
          )}
          {canVoirAlertes && (
          <Button variant="outline" onClick={() => setIsConfigDialogOpen(true)}>
            <Bell className="w-4 h-4 mr-2" />
            Alertes
            {alerts.length > 0 && (
              <Badge variant="destructive" className="ml-2">{alerts.length}</Badge>
            )}
          </Button>
          )}
          {(canEditStock && !retraitSeulm) && (
            <Button onClick={() => {
              setEditingProduct(null);
              setNewProduct({
                name: "", category: "peinture", department: "GLOBAL", unit: "unité",
                quantity: 0, min_threshold: 5, unit_price: 0, location: "", supplier: "",
                product_code: "", phone_number: "", notes: "",
                is_kit: false, kit_items: [], is_returnable: false
              });
              setIsProductDialogOpen(true);
            }}>
              <Plus className="w-4 h-4 mr-2" />
              Nouveau produit
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {canVoirStats && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="pt-4 pb-4 md:pt-6">
            <div className="flex items-center gap-2 md:gap-4">
              <div className="p-2 md:p-3 bg-white/20 rounded-lg shrink-0">
                <Package className="w-4 h-4 md:w-6 md:h-6" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] md:text-sm text-blue-100 truncate">Total Produits</p>
                <p className="text-lg md:text-2xl font-bold">{stats?.total_products || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="pt-4 pb-4 md:pt-6">
            <div className="flex items-center gap-2 md:gap-4">
              <div className="p-2 md:p-3 bg-white/20 rounded-lg shrink-0">
                <DollarSign className="w-4 h-4 md:w-6 md:h-6" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] md:text-sm text-emerald-100 truncate">Valeur Inventaire</p>
                <p className="text-lg md:text-2xl font-bold truncate">{formatCurrency(stats?.total_value || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={stats?.low_stock_count > 0 ? "bg-gradient-to-br from-amber-500 to-amber-600 text-white" : ""}>
          <CardContent className="pt-4 pb-4 md:pt-6">
            <div className="flex items-center gap-2 md:gap-4">
              <div className={`p-2 md:p-3 rounded-lg shrink-0 ${stats?.low_stock_count > 0 ? "bg-white/20" : "bg-amber-100"}`}>
                <AlertTriangle className={`w-4 h-4 md:w-6 md:h-6 ${stats?.low_stock_count > 0 ? "" : "text-amber-600"}`} />
              </div>
              <div className="min-w-0">
                <p className={`text-[11px] md:text-sm truncate ${stats?.low_stock_count > 0 ? "text-amber-100" : "text-slate-500"}`}>Stock Bas</p>
                <p className={`text-lg md:text-2xl font-bold ${stats?.low_stock_count > 0 ? "" : "text-slate-900"}`}>
                  {stats?.low_stock_count || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 pb-4 md:pt-6">
            <div className="flex items-center gap-2 md:gap-4">
              <div className="p-2 md:p-3 bg-purple-100 rounded-lg shrink-0">
                <History className="w-4 h-4 md:w-6 md:h-6 text-purple-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] md:text-sm text-slate-500 truncate">Mouvements (30j)</p>
                <p className="text-lg md:text-2xl font-bold text-slate-900">{movements.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Low Stock Alert — Centre de commande (produits + vêtements) */}
      {canVoirStats && ((stats?.low_stock_count > 0) || vetementsLowStock.length > 0) && (
        <Card className="border-amber-200 bg-amber-50" data-testid="widget-stock-bas">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-amber-900 text-sm sm:text-base">Centre de commande — Stocks sous seuil</h3>
                <p className="text-sm text-amber-700 mb-3">
                  {(stats?.low_stock_count || 0)} produit(s) · {vetementsLowStock.length} vêtement(s) à réapprovisionner
                </p>
                {(stats?.low_stock_products || []).length > 0 && (
                  <div className="mb-2">
                    <p className="text-xs font-medium text-amber-800 mb-1">Produits :</p>
                    <div className="flex flex-wrap gap-2">
                      {stats.low_stock_products.slice(0, 5).map(p => (
                        <Badge key={p.id} variant="outline" className="bg-white" data-testid="badge-produit-bas">
                          {p.name}: {p.quantity} {p.unit}
                        </Badge>
                      ))}
                      {stats.low_stock_products.length > 5 && (
                        <Badge variant="outline" className="bg-white text-slate-500">
                          +{stats.low_stock_products.length - 5} autres
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
                {vetementsLowStock.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-amber-800 mb-1">Vêtements :</p>
                    <div className="flex flex-wrap gap-2">
                      {vetementsLowStock.slice(0, 5).map(v => (
                        <Badge key={`${v.vetement_id}_${v.taille}`} variant="outline" className="bg-white border-blue-200" data-testid="badge-vetement-bas">
                          {v.vetement_nom} {v.taille}: {v.quantite_stock}
                        </Badge>
                      ))}
                      {vetementsLowStock.length > 5 && (
                        <Badge variant="outline" className="bg-white text-slate-500">
                          +{vetementsLowStock.length - 5} autres
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex flex-row sm:flex-col gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="btn-voir-stock-bas"
                  onClick={() => {
                    // Si vêtements uniquement → onglet vêtements. Sinon, produits (peut aussi inclure les 2)
                    if ((stats?.low_stock_count || 0) > 0) {
                      setActiveTab("products");
                      setShowLowStock(true);
                      setTimeout(() => {
                        const el = document.querySelector('[data-testid="produits-grid"]');
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 100);
                    } else if (vetementsLowStock.length > 0) {
                      setActiveTab("vetements");
                    }
                  }}
                >
                  Voir stock bas
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="overflow-x-auto -mx-1 px-1">
        <TabsList className="w-max min-w-full flex-nowrap">
          <TabsTrigger value="products" className="whitespace-nowrap">
            <Package className="w-4 h-4 mr-2" />
            Produits ({filteredProducts.length})
          </TabsTrigger>
          {!produitsSeulm && <TabsTrigger value="vetements" className="whitespace-nowrap">
            <ShirtIcon className="w-4 h-4 mr-2" />
            Items sous Quotas
          </TabsTrigger>}
          {!produitsSeulm && <TabsTrigger value="equipements-sortis" className="whitespace-nowrap" data-testid="tab-equipements-sortis">
            <RotateCcw className="w-4 h-4 mr-2" />
            Équipements sortis {equipementsSortis.length > 0 && (
              <Badge variant="destructive" className="ml-2 h-5 px-1.5">{equipementsSortis.length}</Badge>
            )}
          </TabsTrigger>}
          {!produitsSeulm && <TabsTrigger value="movements" className="whitespace-nowrap">
            <History className="w-4 h-4 mr-2" />
            Mouvements
          </TabsTrigger>}
          {!produitsSeulm && <TabsTrigger value="stats" className="whitespace-nowrap">
            <TrendingUp className="w-4 h-4 mr-2" />
            Statistiques
          </TabsTrigger>}
        </TabsList>
        </div>

        {/* Products Tab */}
        <TabsContent value="products" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Rechercher un produit..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes</SelectItem>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.icon} {c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select 
                  value={departmentFilter} 
                  onValueChange={setDepartmentFilter}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Département" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    {DEPARTMENTS.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!isInstallateur && (
                <Button
                  variant={showLowStock ? "default" : "outline"}
                  onClick={() => setShowLowStock(!showLowStock)}
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Stock bas
                </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* 🆕 2026-05-31 — Barre d'actions impression en lot (visible si admin OU chargée de projet) */}
          {canPrintLabels && filteredProducts.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 bg-slate-50 border rounded-lg px-3 py-2" data-testid="bulk-print-bar">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer select-none">
                <Checkbox
                  checked={allVisibleSelected()}
                  onCheckedChange={toggleSelectAllVisible}
                  data-testid="bulk-select-all"
                />
                Tout sélectionner ({filteredProducts.length})
              </label>
              <span className="text-xs text-slate-500" data-testid="bulk-count">
                {selectedProductIds.size} sélectionné{selectedProductIds.size > 1 ? 's' : ''}
              </span>
              <div className="ml-auto flex gap-2">
                {selectedProductIds.size > 0 && (
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => imprimerEtiquettesEnLot(
                      filteredProducts.filter(p => selectedProductIds.has(p.id))
                    )}
                    data-testid="btn-print-selected"
                  >
                    <Printer className="w-4 h-4 mr-1" />
                    Imprimer les étiquettes sélectionnées ({selectedProductIds.size})
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => imprimerEtiquettesEnLot(filteredProducts)}
                  title={`Imprime toutes les étiquettes (${filteredProducts.length} produits filtrés)`}
                  data-testid="btn-print-all"
                >
                  <Printer className="w-4 h-4 mr-1" />
                  Imprimer toutes les étiquettes
                </Button>
                {/* 🖨 [Phase v13.60.10 — 2026-08-15] Bouton d'impression texte libre.
                    Ouvre une modale avec textarea + réutilise le même pipeline
                    Dymo (89×36 mm landscape, même imprimante, mêmes rôles). */}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setShowFreeTextModal(true)}
                  title="Imprimer une étiquette avec du texte libre (max 3 lignes)"
                  data-testid="btn-print-free-text-open"
                >
                  <Printer className="w-4 h-4 mr-1" />
                  Imprimer du texte libre
                </Button>
              </div>
            </div>
          )}

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="produits-grid">
            {filteredProducts.map(product => (
              <Card 
                key={product.id}
                className={product.quantity <= product.min_threshold ? "border-amber-300 bg-amber-50/50" : ""}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {/* 🆕 2026-05-31 — Checkbox sélection multiple impression (admin + chargée de projet) */}
                      {canPrintLabels && (
                        <Checkbox
                          checked={selectedProductIds.has(product.id)}
                          onCheckedChange={() => toggleProductSelection(product.id)}
                          data-testid={`product-select-${product.id}`}
                          aria-label={`Sélectionner ${product.name} pour impression`}
                        />
                      )}
                      <span className="text-2xl">
                        {product.is_kit ? "📦" : (CATEGORIES.find(c => c.value === product.category)?.icon || "📦")}
                      </span>
                      <div>
                        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                          {product.name}
                          {product.is_kit && (
                            <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-[10px]" data-testid="badge-bac">
                              <Boxes className="w-3 h-3 mr-1" /> Bac
                            </Badge>
                          )}
                          {product.is_returnable && (
                            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]" data-testid="badge-returnable">
                              <RotateCcw className="w-3 h-3 mr-1" /> Réutilisable
                            </Badge>
                          )}
                        </h3>
                        <p className="text-xs text-slate-500">{product.location || "Sans emplacement"}</p>
                        {product.product_code && (
                          <p className="text-xs text-blue-600 font-mono">{product.product_code}</p>
                        )}
                        {/* 🆕 [Phase 27] Contenu libre du kit affiché en preview */}
                        {product.is_kit && product.contenu_libre && (
                          <p className="text-[11px] text-indigo-700 italic mt-1 line-clamp-2" data-testid={`kit-contenu-preview-${product.id}`}>
                            📋 {product.contenu_libre}
                          </p>
                        )}
                      </div>
                    </div>
                    <Badge variant="secondary">{product.department}</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className={`text-3xl font-bold ${product.quantity <= product.min_threshold ? "text-amber-600" : "text-slate-900"}`}>
                        {product.quantity}
                      </p>
                      <p className="text-sm text-slate-500">{product.unit}(s)</p>
                    </div>
                    <div className="text-right">
                      {canVoirStats && (
                        <>
                          <p className="text-lg font-semibold text-emerald-600">
                            {formatCurrency(product.quantity * product.unit_price)}
                          </p>
                          <p className="text-xs text-slate-500">{formatCurrency(product.unit_price)}/unité</p>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {product.quantity <= product.min_threshold && (
                    <div className="flex items-center gap-2 mb-3 text-amber-600 text-sm">
                      <AlertTriangle className="w-4 h-4" />
                      <span>Stock bas (min: {product.min_threshold})</span>
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    {!retraitSeulm && <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => openMovementDialog(product, "in")}
                    >
                      <ArrowUpCircle className="w-4 h-4 mr-1 text-emerald-600" />
                      Entrée
                    </Button>}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => openMovementDialog(product, "out")}
                      disabled={product.quantity <= 0}
                    >
                      <ArrowDownCircle className="w-4 h-4 mr-1 text-red-600" />
                      {retraitSeulm ? "Retirer" : "Sortie"}
                    </Button>
                    {canEditStock && !retraitSeulm && (
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(product)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    )}
                    {/* 🖨 2026-05-19 — Bouton Imprimer étiquette Dymo LabelWriter 550.
                        Encodage du `product.id` dans un code-barres Code128.
                        🔒 2026-05-19 — Réservé aux ADMINS (propriétaire + adjoint_admin).
                        🆕 [Phase 26 P1] Inclus aussi chargée de projet.
                        Les peintres, chefs d'équipe, installateurs et sous-traitants
                        ne doivent pas pouvoir gaspiller des étiquettes ni accéder
                        à la liste complète d'inventaire par ce biais. */}
                    {canPrintLabels && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => imprimerEtiquette(product, e)}
                        title="Imprimer une étiquette (Shift+Clic pour aperçu seul)"
                        data-testid={`btn-print-label-${product.id}`}
                      >
                        <Printer className="w-4 h-4" />
                      </Button>
                    )}
                    {canEditStock && !retraitSeulm && (
                      <Button variant="ghost" size="sm" onClick={() => deleteProduct(product.id)} className="text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {filteredProducts.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-slate-500">
                <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Aucun produit trouvé</p>
                {canEditStock && (
                  <Button className="mt-4" onClick={() => setIsProductDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un produit
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* 🆕 Équipements sortis Tab */}
        <TabsContent value="equipements-sortis" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-emerald-600" />
                Équipements actuellement sortis
              </CardTitle>
              <p className="text-sm text-slate-500">
                Équipements réutilisables ou bacs (kits) encore en dehors de l'entrepôt. Cliquez sur <b>Remettre</b> quand ils reviennent.
              </p>
            </CardHeader>
            <CardContent>
              {equipementsSortis.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <RotateCcw className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Aucun équipement sorti.</p>
                  <p className="text-xs mt-1">Tous les équipements sont dans l'entrepôt.</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="liste-equipements-sortis">
                  {equipementsSortis.map((item, idx) => (
                    <div
                      key={`${item.product_id}_${item.projet_id || 'none'}_${idx}`}
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg hover:bg-slate-50 gap-3"
                      data-testid={`equipement-sorti-${item.product_id}`}
                    >
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="p-2 bg-amber-100 rounded-lg shrink-0">
                          {item.is_kit ? <Boxes className="w-5 h-5 text-amber-600" /> : <Package className="w-5 h-5 text-amber-600" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-900">{item.product_name}</p>
                          <div className="flex flex-wrap gap-2 text-xs text-slate-500 mt-1">
                            <Badge variant="outline" className="text-xs">
                              Solde sorti : <b className="ml-1 text-red-600">{item.solde}</b> {item.unit || ""}
                            </Badge>
                            {item.taken_by && (
                              <Badge variant="outline" className="text-xs">
                                <User className="w-3 h-3 mr-1" /> {item.taken_by}
                              </Badge>
                            )}
                            {item.project && (
                              <Badge variant="outline" className="text-xs">🏗️ {item.project}</Badge>
                            )}
                            {item.date_sortie && (
                              <span className="text-slate-400">Depuis {item.date_sortie}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      {canRemettreEquip && (
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => openReturnDialog(item)}
                          data-testid={`btn-remettre-${item.product_id}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Remettre
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vêtements Tab */}
        <TabsContent value="vetements">
          <VetementsInventory />
        </TabsContent>

        {/* Movements Tab */}
        <TabsContent value="movements">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Historique des mouvements
              </CardTitle>
            </CardHeader>
            <CardContent>
              {movements.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Aucun mouvement enregistré</p>
              ) : (
                <div className="space-y-3">
                  {movements.map(m => (
                    <div 
                      key={m.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${m.movement_type === "in" ? "bg-emerald-100" : "bg-red-100"}`}>
                          {m.movement_type === "in" ? (
                            <ArrowUpCircle className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <ArrowDownCircle className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{m.product_name}</p>
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <span>{m.date}</span>
                            {m.taken_by && (
                              <Badge variant="outline" className="text-xs">
                                <User className="w-3 h-3 mr-1" />
                                {m.taken_by}
                              </Badge>
                            )}
                            {m.department && (
                              <Badge variant="secondary" className="text-xs">
                                {m.department}
                              </Badge>
                            )}
                            {m.project && (
                              <span className="text-slate-400">• {m.project}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-bold ${m.movement_type === "in" ? "text-emerald-600" : "text-red-600"}`}>
                          {m.movement_type === "in" ? "+" : "-"}{m.quantity}
                        </p>
                        <p className="text-xs text-slate-500">
                          {m.previous_qty} → {m.new_qty}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stats Tab */}
        <TabsContent value="stats">
          {/* Carte récap Items sous Quotas */}
          {(() => {
            const totalQte = vetementsStockAll.reduce((s, v) => s + (v.quantite_stock || 0), 0);
            const totalValeur = vetementsStockAll.reduce((s, v) => {
              const m = vetementModels.find(mm => mm.id === v.vetement_id);
              return s + (v.quantite_stock || 0) * (m?.prix_unitaire || 0);
            }, 0);
            const nbModels = vetementModels.length;
            return (
              <Card className="mb-6 border-blue-200 bg-blue-50/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShirtIcon className="w-5 h-5 text-blue-600" />
                    Items sous Quotas — Valorisation stock
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-3 bg-white rounded-lg border">
                      <p className="text-xs text-slate-500">Modèles configurés</p>
                      <p className="text-2xl font-bold text-slate-900" data-testid="stats-items-models">{nbModels}</p>
                    </div>
                    <div className="p-3 bg-white rounded-lg border">
                      <p className="text-xs text-slate-500">Quantité totale en stock</p>
                      <p className="text-2xl font-bold text-slate-900" data-testid="stats-items-qty">{totalQte}</p>
                    </div>
                    <div className="p-3 bg-white rounded-lg border">
                      <p className="text-xs text-slate-500">Valeur totale</p>
                      <p className="text-2xl font-bold text-emerald-600" data-testid="stats-items-valeur">{formatCurrency(totalValeur)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })()}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Par catégorie */}
            <Card>
              <CardHeader>
                <CardTitle>Valeur par catégorie</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats?.by_category && Object.entries(stats.by_category).map(([cat, data]) => (
                    <div key={cat} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">
                          {CATEGORIES.find(c => c.value === cat)?.icon || "📦"}
                        </span>
                        <div>
                          <p className="font-medium capitalize">{cat}</p>
                          <p className="text-sm text-slate-500">{data.count} produits</p>
                        </div>
                      </div>
                      <p className="text-lg font-bold text-emerald-600">
                        {formatCurrency(data.value)}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Par département */}
            <Card>
              <CardHeader>
                <CardTitle>Valeur par département</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats?.by_department && Object.entries(stats.by_department).map(([dept, data]) => (
                    <div key={dept} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-slate-100 rounded-lg">
                          <Building2 className="w-5 h-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium">{dept}</p>
                          <p className="text-sm text-slate-500">{data.count} produits</p>
                        </div>
                      </div>
                      <p className="text-lg font-bold text-emerald-600">
                        {formatCurrency(data.value)}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Product Dialog */}
      <Dialog open={isProductDialogOpen} onOpenChange={setIsProductDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingProduct ? "Modifier le produit" : "Nouveau produit"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nom du produit *</Label>
              <Input
                value={newProduct.name}
                onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                placeholder="Ex: Peinture Bétonel Blanc"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Catégorie</Label>
                <Select value={newProduct.category} onValueChange={(v) => setNewProduct({...newProduct, category: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.icon} {c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Département</Label>
                <Select value={newProduct.department} onValueChange={(v) => setNewProduct({...newProduct, department: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTMENTS.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label>Quantité</Label>
                <Input
                  type="number"
                  value={newProduct.quantity}
                  onChange={(e) => setNewProduct({...newProduct, quantity: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Unité</Label>
                <Select value={newProduct.unit} onValueChange={(v) => setNewProduct({...newProduct, unit: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map(u => (
                      <SelectItem key={u} value={u}>{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Seuil alerte</Label>
                <Input
                  type="number"
                  value={newProduct.min_threshold}
                  onChange={(e) => setNewProduct({...newProduct, min_threshold: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prix unitaire ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newProduct.unit_price}
                  onChange={(e) => setNewProduct({...newProduct, unit_price: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Emplacement</Label>
                <Input
                  value={newProduct.location}
                  onChange={(e) => setNewProduct({...newProduct, location: e.target.value})}
                  placeholder="Ex: Étagère A-3"
                />
              </div>
            </div>
            
            <div>
              <Label>Fournisseur</Label>
              <Input
                value={newProduct.supplier}
                onChange={(e) => setNewProduct({...newProduct, supplier: e.target.value})}
                placeholder="Ex: Canac, BMR, etc."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Code produit</Label>
                <Input
                  value={newProduct.product_code}
                  onChange={(e) => setNewProduct({...newProduct, product_code: e.target.value})}
                  placeholder="Ex: PNT-001"
                  data-testid="input-product-code"
                />
              </div>
              <div>
                <Label>Téléphone fournisseur</Label>
                <Input
                  value={newProduct.phone_number}
                  onChange={(e) => setNewProduct({...newProduct, phone_number: e.target.value})}
                  placeholder="Ex: 418-555-1234"
                  data-testid="input-phone-number"
                />
              </div>
            </div>

            {/* 🆕 Bac (Kit) & Équipement réutilisable */}
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="is_returnable"
                  checked={!!newProduct.is_returnable}
                  onCheckedChange={(v) => setNewProduct({...newProduct, is_returnable: !!v})}
                  data-testid="checkbox-is-returnable"
                />
                <Label htmlFor="is_returnable" className="cursor-pointer flex-1">
                  <span className="font-medium">Équipement réutilisable</span>
                  <p className="text-xs text-slate-500">Doit être remis à l'entrepôt après usage (apparaît dans « Équipements sortis »).</p>
                </Label>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="is_kit"
                  checked={!!newProduct.is_kit}
                  onCheckedChange={(v) => setNewProduct({...newProduct, is_kit: !!v, kit_items: v ? (newProduct.kit_items || []) : []})}
                  data-testid="checkbox-is-kit"
                />
                <Label htmlFor="is_kit" className="cursor-pointer flex-1">
                  <span className="font-medium flex items-center gap-2"><Boxes className="w-4 h-4" /> C'est un Bac (Kit)</span>
                  <p className="text-xs text-slate-500">Regroupe plusieurs items. Sortir/remettre le bac affecte automatiquement les stocks des items.</p>
                </Label>
              </div>

              {newProduct.is_kit && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 space-y-3" data-testid="kit-items-editor">
                  {/* 🆕 [Phase 27] Contenu libre — texte descriptif manuel du bac/kit */}
                  <div>
                    <Label className="text-xs font-medium text-indigo-900 mb-1 block">
                      Contenu du bac / kit (texte libre)
                    </Label>
                    <textarea
                      value={newProduct.contenu_libre || ''}
                      onChange={(e) => setNewProduct({...newProduct, contenu_libre: e.target.value})}
                      placeholder="Ex: 3 rouleaux, 2 manchons 9mm, 1 bac trempage…"
                      className="w-full text-sm bg-white border border-indigo-200 rounded-md px-2 py-1.5 min-h-[70px] focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      data-testid="kit-contenu-libre"
                    />
                    <p className="text-[10px] text-indigo-700 mt-1">
                      Source de vérité du contenu. Les items entrepôt ci-dessous sont optionnels (utiles pour le calcul de stock automatique).
                    </p>
                  </div>

                  <div className="border-t border-indigo-200 pt-2">
                    <p className="text-xs font-medium text-indigo-900">Items entrepôt liés (optionnel) :</p>
                  {(newProduct.kit_items || []).map((ki, idx) => {
                    const child = products.find(p => p.id === ki.product_id);
                    return (
                      <div key={idx} className="flex items-center gap-2">
                        <Select
                          value={ki.product_id || ""}
                          onValueChange={(v) => {
                            const arr = [...(newProduct.kit_items || [])];
                            arr[idx] = {...arr[idx], product_id: v};
                            setNewProduct({...newProduct, kit_items: arr});
                          }}
                        >
                          <SelectTrigger className="flex-1 bg-white">
                            <SelectValue placeholder="Choisir un produit" />
                          </SelectTrigger>
                          <SelectContent>
                            {products.filter(p => !p.is_kit && p.id !== editingProduct?.id).map(p => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.name} {p.unit ? `(${p.unit})` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          min="0"
                          step="0.1"
                          value={ki.quantity}
                          onChange={(e) => {
                            const arr = [...(newProduct.kit_items || [])];
                            arr[idx] = {...arr[idx], quantity: parseFloat(e.target.value) || 0};
                            setNewProduct({...newProduct, kit_items: arr});
                          }}
                          className="w-20 bg-white"
                          placeholder="Qté"
                        />
                        <span className="text-xs text-slate-500 w-16 truncate">{child?.unit || 'unité'}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const arr = (newProduct.kit_items || []).filter((_, i) => i !== idx);
                            setNewProduct({...newProduct, kit_items: arr});
                          }}
                          className="text-red-500"
                          data-testid={`btn-remove-kit-item-${idx}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const arr = [...(newProduct.kit_items || []), { product_id: "", quantity: 1 }];
                      setNewProduct({...newProduct, kit_items: arr});
                    }}
                    data-testid="btn-add-kit-item"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Ajouter un item au bac
                  </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 🧰 [Phase 17.5] Section "Normes par unité" — owner uniquement, article existant */}
          <NormesArticleSection articleId={editingProduct?.id} canEdit={isAdmin} />

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsProductDialogOpen(false)}>Annuler</Button>
            <Button onClick={saveProduct}>
              {editingProduct ? "Sauvegarder" : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movement Dialog */}
      <Dialog open={isMovementDialogOpen} onOpenChange={(open) => { setIsMovementDialogOpen(open); if (!open) setProjetSearch(""); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {movementType === "in" ? (
                <span className="flex items-center gap-2">
                  <ArrowUpCircle className="w-5 h-5 text-emerald-600" />
                  Entrée de stock
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <ArrowDownCircle className="w-5 h-5 text-red-600" />
                  Sortie de stock
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {movementProduct && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-100 rounded-lg">
                <p className="font-medium">{movementProduct.name}</p>
                <p className="text-sm text-slate-500">
                  Stock actuel: {movementProduct.quantity} {movementProduct.unit}(s)
                </p>
              </div>
              
              <div>
                <Label>Quantité *</Label>
                <Input
                  type="number"
                  min="1"
                  max={movementType === "out" ? movementProduct.quantity : undefined}
                  value={newMovement.quantity}
                  onChange={(e) => setNewMovement({...newMovement, quantity: parseFloat(e.target.value) || 0})}
                />
              </div>
              
              {movementType === "out" ? (
                <>
                  {!isInstallateur ? (
                  <div>
                    <Label>Lier à un projet</Label>
                    {/* 🆕 2026-05-14 — Recherche libre dans tous les projets actifs.
                        Avant, le chef d'équipe ne voyait que 3 projets (slice(0,3)) sans pouvoir
                        en chercher d'autres. Maintenant: un input filtre la liste à la volée. */}
                    <Input
                      type="search"
                      placeholder="🔍 Rechercher par client, projet, n° dossier…"
                      value={projetSearch}
                      onChange={(e) => setProjetSearch(e.target.value)}
                      className="mb-2"
                      data-testid="entrepot-projet-search"
                    />
                    <Select
                      value={newMovement.projet_id || "__none__"}
                      onValueChange={(v) => {
                        if (v === "__none__") {
                          setNewMovement({...newMovement, projet_id: "", project: ""});
                        } else {
                          const projet = projets.find(p => p.id === v);
                          setNewMovement({
                            ...newMovement,
                            projet_id: v,
                            project: projet ? `${projet.client_nom} – ${projet.nom_dossier}` : ""
                          });
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un projet (optionnel)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Aucun projet</SelectItem>
                        {(() => {
                          const q = projetSearch.trim().toLowerCase();
                          const filtered = q
                            ? projets.filter(p => {
                                const haystack = [
                                  p.client_nom, p.nom_dossier, p.numero_dossier,
                                  p.adresse_travaux, p.ville_travaux
                                ].filter(Boolean).join(' ').toLowerCase();
                                return haystack.includes(q);
                              })
                            : projets;
                          return filtered.slice(0, 200).map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.client_nom} – {p.nom_dossier}{p.numero_dossier ? ` (${p.numero_dossier})` : ''}
                            </SelectItem>
                          ));
                        })()}
                      </SelectContent>
                    </Select>
                    {projets.length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">Aucun projet actif trouvé.</p>
                    )}
                  </div>
                  ) : (
                  <div>
                    <p className="text-xs text-slate-500">Ce retrait sera lié à votre semaine en cours.</p>
                  </div>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <Label>Fournisseur</Label>
                    <Input
                      value={newMovement.supplier}
                      onChange={(e) => setNewMovement({...newMovement, supplier: e.target.value})}
                      placeholder="Ex: Canac"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>No. Facture</Label>
                      <Input
                        value={newMovement.invoice_number}
                        onChange={(e) => setNewMovement({...newMovement, invoice_number: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Coût unitaire ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newMovement.unit_cost}
                        onChange={(e) => setNewMovement({...newMovement, unit_cost: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMovementDialogOpen(false)}>Annuler</Button>
            <Button onClick={recordMovement} className={movementType === "in" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}>
              {movementType === "in" ? "Enregistrer l'entrée" : "Enregistrer la sortie"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Return Dialog (Remise d'équipement) */}
      <Dialog open={isReturnDialogOpen} onOpenChange={setIsReturnDialogOpen}>
        <DialogContent className="max-w-md" data-testid="dialog-remise">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-emerald-600" />
              Remettre l'équipement
            </DialogTitle>
          </DialogHeader>

          {returnItem && (
            <div className="space-y-4">
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="font-medium flex items-center gap-2">
                  {returnItem.is_kit ? <Boxes className="w-4 h-4 text-emerald-700" /> : <Package className="w-4 h-4 text-emerald-700" />}
                  {returnItem.product_name}
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  Solde sorti : <b className="text-red-600">{returnItem.solde} {returnItem.unit}</b>
                </p>
                {returnItem.project && (
                  <p className="text-xs text-slate-500 mt-1">Projet : {returnItem.project}</p>
                )}
                {returnItem.taken_by && (
                  <p className="text-xs text-slate-500">Sorti par : {returnItem.taken_by}</p>
                )}
              </div>

              <div>
                <Label>Quantité à remettre *</Label>
                <Input
                  type="number"
                  min="1"
                  max={returnItem.solde}
                  value={returnQty}
                  onChange={(e) => setReturnQty(parseFloat(e.target.value) || 0)}
                  data-testid="input-qty-remise"
                />
                <p className="text-xs text-slate-500 mt-1">Maximum : {returnItem.solde}</p>
              </div>

              <div>
                <Label>Notes (optionnel)</Label>
                <Input
                  value={returnNotes}
                  onChange={(e) => setReturnNotes(e.target.value)}
                  placeholder="Ex: État OK / pièce manquante / endommagé..."
                  data-testid="input-notes-remise"
                />
              </div>

              {returnItem.is_kit && (
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded text-xs text-indigo-900">
                  <Boxes className="w-4 h-4 inline mr-1" />
                  Les items du bac seront automatiquement remis au stock.
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReturnDialogOpen(false)}>Annuler</Button>
            <Button
              onClick={recordReturn}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-confirm-remise"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Confirmer la remise
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert Config Dialog */}
      <Dialog open={isConfigDialogOpen} onOpenChange={setIsConfigDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Configuration des alertes
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Alertes activées</p>
                <p className="text-sm text-slate-500">Recevoir des alertes quand le stock est bas</p>
              </div>
              <Button
                variant={alertConfig.enabled ? "default" : "outline"}
                size="sm"
                onClick={() => setAlertConfig({...alertConfig, enabled: !alertConfig.enabled})}
              >
                {alertConfig.enabled ? "Activé" : "Désactivé"}
              </Button>
            </div>
            
            {/* Email toujours visible */}
            <div>
              <Label>Courriel destinataire des alertes</Label>
              <Input
                type="email"
                value={alertConfig.email}
                onChange={(e) => setAlertConfig({...alertConfig, email: e.target.value})}
                placeholder="admin@peintureresidentielleqc.com"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">L'alerte sera envoyée à cette adresse dès qu'un produit passe sous son seuil minimum.</p>
            </div>
            
            <div>
              <Label>Fréquence</Label>
              <Select value={alertConfig.frequency || "instant"} onValueChange={(v) => setAlertConfig({...alertConfig, frequency: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="instant">Instantanée (dès le dépassement)</SelectItem>
                  <SelectItem value="daily">Résumé quotidien</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {alerts.length > 0 && (
              <div>
                <Label>Alertes en cours ({alerts.length})</Label>
                <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                  {alerts.map(a => (
                    <div key={a.id} className="flex items-center justify-between p-2 bg-amber-50 rounded border border-amber-200">
                      <div>
                        <p className="text-sm font-medium">{a.product_name}</p>
                        <p className="text-xs text-slate-500">Stock: {a.current_qty} (min: {a.min_threshold})</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConfigDialogOpen(false)}>Annuler</Button>
            <Button onClick={saveAlertConfig}>Sauvegarder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🗑️ [Phase 19A] Dialog Corbeille articles (owner uniquement) */}
      {isProprietaire && (
        <CorbeilleArticlesDialog
          open={isCorbeilleOpen}
          onClose={() => setIsCorbeilleOpen(false)}
          onRestored={() => {
            setCorbeilleCount(c => Math.max(0, c - 1));
            loadData();
          }}
        />
      )}

      {/* 🖨 [Phase v13.60.10 — 2026-08-15] Modale d'impression de texte libre.
          Réutilise le pipeline Dymo 89×36mm. Accessible aux mêmes rôles que
          canPrintLabels (owner + admin + charge_projet). */}
      <Dialog open={showFreeTextModal} onOpenChange={setShowFreeTextModal}>
        <DialogContent className="max-w-lg" data-testid="dialog-print-free-text">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-blue-600" />
              Imprimer une étiquette de texte libre
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-slate-500 leading-relaxed">
              Saisissez le texte à imprimer (ex : « À livrer lundi », « Retour
              fournisseur »). La police s&apos;ajuste automatiquement pour remplir
              l&apos;étiquette Dymo (89 × 36 mm). Max 2-3 lignes recommandé.
            </p>
            <div>
              <Label htmlFor="free-text-input" className="text-sm font-medium">
                Texte
              </Label>
              <textarea
                id="free-text-input"
                data-testid="free-text-input"
                value={freeTextValue}
                onChange={(e) => setFreeTextValue(e.target.value)}
                rows={3}
                maxLength={140}
                placeholder="Ex : À livrer lundi&#10;Client Tremblay"
                autoFocus
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-base text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
              />
              <div className="flex justify-between items-center mt-1">
                <p className="text-[11px] text-slate-400 italic">
                  Retours à la ligne respectés (Entrée).
                </p>
                <p className="text-[11px] text-slate-400 tabular-nums" data-testid="free-text-counter">
                  {freeTextValue.length}/140
                </p>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowFreeTextModal(false);
                setFreeTextValue("");
              }}
              disabled={freeTextPrinting}
              data-testid="btn-print-free-text-cancel"
            >
              Annuler
            </Button>
            <Button
              onClick={imprimerTexteLibre}
              disabled={freeTextPrinting || !freeTextValue.trim()}
              data-testid="btn-print-free-text-confirm"
            >
              {freeTextPrinting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Impression…</>
              ) : (
                <><Printer className="w-4 h-4 mr-2" /> Imprimer</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Entrepot;
